import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertTriangle, Users, CreditCard, Ban } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-slate-50 pt-32 pb-20">
      {/* Futuristic grid background */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #1e293b 1px, transparent 1px),
            linear-gradient(to bottom, #1e293b 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-2xl mb-6 shadow-xl shadow-cyan-500/30"
          >
            <FileText className="w-10 h-10 text-white" />
          </motion.div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-4">
            Terms & Conditions
          </h1>
          
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="h-1 bg-gradient-to-r from-cyan-600 to-blue-600 mx-auto rounded-full mb-4"
          />
          
          <p className="text-slate-600 text-lg">Last updated: March 25, 2026</p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 p-8 md:p-12"
        >
          <div className="prose prose-slate max-w-none">
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <Scale className="w-7 h-7 mr-3 text-cyan-600" />
                Agreement to Terms
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                These Terms and Conditions ("Terms") constitute a legally binding agreement between you (the "Merchant" or "User") and TransactWorld Limited ("TransactWorld," "we," "us," or "our") governing your access to and use of our payment gateway services, website, and related services (collectively, the "Services"). By creating an account or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms.
              </p>
              <p className="text-slate-700 leading-relaxed text-justify">
                If you do not agree to these Terms, you must not access or use our Services. We reserve the right to update these Terms at any time, and your continued use of the Services constitutes acceptance of any modifications.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <Users className="w-7 h-7 mr-3 text-cyan-600" />
                Eligibility and Account Registration
              </h2>
              <div className="space-y-4">
                <div className="p-5 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Eligibility Requirements</h3>
                  <p className="text-slate-700 leading-relaxed text-justify">
                    To use our Services, you must be a legally registered business entity or an individual at least 18 years of age with the legal capacity to enter into binding contracts. You must provide accurate, complete, and current information during the registration process and maintain the accuracy of such information throughout your use of the Services.
                  </p>
                </div>
                
                <div className="p-5 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">Account Security</h3>
                  <p className="text-slate-700 leading-relaxed text-justify">
                    You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account or any other security breach. We will not be liable for any loss or damage arising from your failure to comply with these security obligations.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <CreditCard className="w-7 h-7 mr-3 text-cyan-600" />
                Payment Processing Services
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                TransactWorld provides payment gateway services that enable you to accept and process electronic payments from your customers. By using our Services, you authorize us to:
              </p>
              <ul className="list-none space-y-2 mb-4">
                {[
                  'Process payment transactions on your behalf',
                  'Hold funds in accordance with our settlement schedule',
                  'Deduct applicable fees and charges from your transactions',
                  'Return or refund transactions as necessary',
                  'Share transaction information with relevant financial institutions',
                  'Implement fraud prevention and risk management measures'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-cyan-50/50 to-blue-50/50 rounded-lg">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Fees and Charges</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                You agree to pay all fees and charges associated with your use of the Services as set forth in your merchant agreement or as posted on our website. Our fees typically include:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {[
                  { title: 'Transaction Fees', desc: 'Percentage-based fee on each successful transaction' },
                  { title: 'Monthly Fees', desc: 'Fixed monthly service and maintenance fees' },
                  { title: 'Chargeback Fees', desc: 'Fees for disputed transactions' },
                  { title: 'Currency Conversion', desc: 'Fees for multi-currency transactions' }
                ].map((fee, idx) => (
                  <div key={idx} className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100">
                    <h4 className="font-bold text-slate-900 mb-2">{fee.title}</h4>
                    <p className="text-slate-700 text-sm">{fee.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed text-justify">
                We reserve the right to modify our fees with 30 days' prior notice. All fees are non-refundable unless otherwise stated in writing.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Merchant Obligations and Prohibited Activities</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                As a merchant using our Services, you agree to:
              </p>
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100 p-6 mb-4">
                <ul className="space-y-2 text-slate-700">
                  <li>\u2022 Comply with all applicable laws, regulations, and card network rules</li>
                  <li>\u2022 Provide accurate product/service descriptions to your customers</li>
                  <li>\u2022 Honor your refund and return policies</li>
                  <li>\u2022 Maintain PCI DSS compliance standards</li>
                  <li>\u2022 Respond promptly to customer disputes and chargebacks</li>
                  <li>\u2022 Maintain adequate records of all transactions</li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <Ban className="w-7 h-7 mr-3 text-cyan-600" />
                Prohibited Businesses
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                You may not use our Services in connection with the following prohibited businesses or activities:
              </p>
              <div className="bg-red-50 rounded-xl border border-red-200 p-6">
                <ul className="grid md:grid-cols-2 gap-2 text-slate-700 text-sm">
                  <li>\u2022 Illegal goods or services</li>
                  <li>\u2022 Adult content and services</li>
                  <li>\u2022 Gambling and gaming</li>
                  <li>\u2022 Cryptocurrency trading (unless approved)</li>
                  <li>\u2022 Money laundering or fraud</li>
                  <li>\u2022 Weapons and ammunition</li>
                  <li>\u2022 Counterfeit goods</li>
                  <li>\u2022 Pyramid or Ponzi schemes</li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Chargebacks and Disputes</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                A chargeback occurs when a customer disputes a transaction with their card issuer. You are responsible for all chargebacks and associated fees. We reserve the right to:
              </p>
              <ul className="list-none space-y-2">
                {[
                  'Debit your account for chargeback amounts and fees',
                  'Hold funds in reserve to cover potential chargebacks',
                  'Suspend or terminate services if chargeback rates exceed acceptable thresholds',
                  'Require additional documentation to contest chargebacks'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-cyan-50/50 to-blue-50/50 rounded-lg">
                    <div className="w-2 h-2 bg-cyan-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Termination</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                Either party may terminate this agreement with 30 days' written notice. We reserve the right to immediately suspend or terminate your account if:
              </p>
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100 p-6">
                <ul className="space-y-2 text-slate-700">
                  <li>\u2022 You breach these Terms or any applicable laws</li>
                  <li>\u2022 Your account shows suspicious or fraudulent activity</li>
                  <li>\u2022 Your chargeback ratio exceeds acceptable limits</li>
                  <li>\u2022 You provide false or misleading information</li>
                  <li>\u2022 Required by law or regulatory authorities</li>
                </ul>
              </div>
              <p className="text-slate-700 leading-relaxed text-justify mt-4">
                Upon termination, we will settle any outstanding funds (minus applicable fees, reserves, and chargebacks) within 30-90 days.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Limitation of Liability</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                To the maximum extent permitted by law, TransactWorld shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities. Our total liability for any claims arising from or related to the Services shall not exceed the fees paid by you in the 12 months preceding the claim.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                <AlertTriangle className="w-7 h-7 mr-3 text-cyan-600" />
                Indemnification
              </h2>
              <p className="text-slate-700 leading-relaxed text-justify">
                You agree to indemnify, defend, and hold harmless TransactWorld and its affiliates, officers, directors, employees, and agents from any claims, losses, damages, liabilities, and expenses (including reasonable attorneys' fees) arising from your use of the Services, violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Governing Law and Dispute Resolution</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                These Terms shall be governed by and construed in accordance with the laws of Cyprus. Any disputes arising from these Terms shall be resolved through binding arbitration in Nicosia, Cyprus, in accordance with the rules of the Cyprus Arbitration and Mediation Centre.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Contact Information</h2>
              <p className="text-slate-700 leading-relaxed text-justify mb-4">
                For questions or concerns regarding these Terms and Conditions, please contact us:
              </p>
              <div className="p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-100">
                <p className="text-slate-800 font-semibold mb-2">Legal Department</p>
                <p className="text-slate-700">TransactWorld Limited</p>
                <p className="text-slate-700">Email: legal@transactworld.com</p>
                <p className="text-slate-700">Phone: +44 20 3129 7109</p>
                <p className="text-slate-700 mt-2">Address: 3 Thivon Street, Anna Court 8-9, Office 202, CY-1056 Nicosia, Cyprus</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsConditions;
